import { useQuery } from '@tanstack/react-query';
import { Checkbox, Form, Input, InputNumber, Modal } from 'antd';
import { useMemo, useState } from 'react';

import { apiFetch } from '@/api/client';
import { rolesKey, useRoles, type Role } from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { FormDrawer } from '@/components/FormDrawer';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { blankToNull } from '@core/format/text';
import { canWrite } from '@core/session/capability';
import type { Session } from '@core/session/types';
import {
  grantCount,
  has,
  moduleState,
  setModule,
  toGrantSet,
  toPermissionList,
  toggle,
  unlistedGrants,
  type GrantKey,
} from '@core/permissionMatrix';

interface PermissionCatalogue {
  modules: string[];
  actions: string[];
}

type Editing = { mode: 'create' } | { mode: 'edit'; role: Role };

/**
 * Role and permission administration (spec Section 4).
 *
 * Roles are tenant data, the catalogue they draw on is code — so this screen creates and grants,
 * but never invents a module or an action. What it must get right is the difference between the
 * three kinds of role: a system role may be re-granted but not renamed or deleted (provisioning
 * and support rely on its code existing), an administrator role holds everything implicitly
 * including modules that do not exist yet, and an ordinary role is entirely the customer's.
 */
export function RolesPage({ session }: { session: Session }) {
  const [editing, setEditing] = useState<Editing | null>(null);
  const [grantingTo, setGrantingTo] = useState<number | null>(null);
  const [cappingId, setCappingId] = useState<number | null>(null);

  const roles = useRoles();
  const granting = roles.data?.find((role) => role.id === grantingTo);
  const capping = roles.data?.find((role) => role.id === cappingId);

  const remove = useApiMutation<void, Role>({
    mutationFn: (role) => apiFetch<void>(`/api/role/${role.id}`, { method: 'DELETE' }),
    success: (_result, role) => `${role.name} deleted.`,
    invalidates: [rolesKey],
  });

  const columns: DataViewColumn<Role>[] = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 170,
      render: (_, row) => <span className="font-mono-nums font-semibold">{row.code}</span>,
    },
    {
      title: 'Role',
      dataIndex: 'name',
      // The card heading: the name plus the badges that say what kind of role it is.
      primary: true,
      render: (_, row) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{row.name}</span>
          {row.isAdministrator && <StatusBadge tone="brand">Administrator</StatusBadge>}
          {row.isSystemRole && <StatusBadge tone="neutral">System</StatusBadge>}
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      responsive: ['xl'],
      ellipsis: true,
      // Off the card. It is already `ellipsis` + `xl`-only, i.e. the one column the page is
      // willing to truncate, and a truncated sentence in a two-column grid says nothing.
      hideOnCard: true,
      render: (_, row) => row.description ?? '—',
    },
    {
      title: 'Users',
      dataIndex: 'assignedUserCount',
      width: 80,
      align: 'right',
      render: (_, row) => <span className="font-mono-nums">{row.assignedUserCount}</span>,
    },
    {
      title: 'Max discount',
      dataIndex: 'maxDiscountPct',
      width: 150,
      align: 'right',
      responsive: ['lg'],
      render: (_, row) => <DiscountCeiling role={row} />,
    },
    {
      title: 'Permissions',
      key: 'permissions',
      width: 120,
      align: 'right',
      render: (_, row) =>
        row.isAdministrator ? (
          <StatusBadge tone="brand">Everything</StatusBadge>
        ) : (
          <span className="font-mono-nums">{grantCount(row.permissions)}</span>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      trailing: true,
      align: 'right',
      fixed: 'right',
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <WriteAction session={session} required="Role:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-shield-check"
                label={`Permissions for ${row.name}`}
                disabled={disabled}
                onClick={() => setGrantingTo(row.id)}
              />
            )}
          </WriteAction>

          {/*
            Not a RecordAction with a `forbidden` for system roles, unlike Rename beside it. The
            ceiling is deliberately editable on a system role — a shop that wants its Counter Staff
            on 2% should not have to clone the role to say so — so the only gate here is Role:Edit.
          */}
          <WriteAction session={session} required="Role:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-percent"
                label={
                  row.isAdministrator
                    ? 'An administrator role has no ceiling'
                    : `Set the discount cap for ${row.name}`
                }
                disabled={disabled || row.isAdministrator}
                onClick={() => setCappingId(row.id)}
              />
            )}
          </WriteAction>

          <RecordAction
            session={session}
            required="Role:Edit"
            // Section 4: a system role can be granted more, but its name and code are what
            // provisioning and support scripts refer to.
            forbidden={row.isSystemRole ? 'A system role cannot be renamed' : undefined}
          >
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Rename ${row.name}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', role: row })}
              />
            )}
          </RecordAction>

          <RecordAction
            session={session}
            required="Role:Delete"
            forbidden={
              row.isSystemRole
                ? 'A system role cannot be deleted'
                : row.assignedUserCount > 0
                  ? `Held by ${row.assignedUserCount} user${row.assignedUserCount === 1 ? '' : 's'}`
                  : undefined
            }
          >
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.name}`}
                title="Delete this role?"
                description="Nobody holds it, so no access changes."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  if (roles.isError) {
    return <ApiErrorAlert error={roles.error} />;
  }

  return (
    <>
      <PageHeading
        title="Roles"
        subtitle="What a job may do. Assign them to people on the Users screen."
        actions={
          <WriteAction session={session} required="Role:Create">
            {(disabled) => (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                disabled={disabled}
                onClick={() => setEditing({ mode: 'create' })}
              >
                New role
              </Button>
            )}
          </WriteAction>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <DataView<Role>
          columns={columns}
          dataSource={roles.data ?? []}
          loading={roles.isLoading}
          empty={
            <EmptyState
              icon="icon icon-shield-check"
              title="No roles yet"
              hint="A user with no role can sign in and do nothing, so roles come before people."
            />
          }
        />
      </Card>

      {editing && <RoleDrawer editing={editing} onClose={() => setEditing(null)} />}

      {granting && (
        <PermissionsDrawer
          role={granting}
          readOnly={!canWrite(session)}
          onClose={() => setGrantingTo(null)}
        />
      )}

      {capping && <DiscountCeilingModal role={capping} onClose={() => setCappingId(null)} />}
    </>
  );
}

/** Whether a role can discount at all — the other axis, and the one that makes the cap mean something. */
function canDiscount(role: Role): boolean {
  return role.isAdministrator || role.permissions.some((p) => p.action === 'Discount');
}

/**
 * The ceiling, read as a number and as a verdict.
 *
 * The interesting cell is not the figure, it is the disagreement: a role that may discount and is
 * capped at zero has been given a button that can only ever be refused, and a role capped above
 * zero that cannot discount has a number nobody will ever reach. Both are misconfigurations that
 * look fine in a permission grid, so this is where they get said out loud.
 */
function DiscountCeiling({ role }: { role: Role }) {
  if (role.isAdministrator) {
    return <StatusBadge tone="brand">No cap</StatusBadge>;
  }

  const allowed = canDiscount(role);

  if (!allowed && role.maxDiscountPct === 0) {
    return <span className="text-default">—</span>;
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-mono-nums">{formatPct(role.maxDiscountPct)}%</span>
      {allowed && role.maxDiscountPct === 0 && (
        <StatusBadge tone="warning">May discount 0%</StatusBadge>
      )}
      {!allowed && role.maxDiscountPct > 0 && <StatusBadge tone="neutral">No grant</StatusBadge>}
    </span>
  );
}

/** Trailing zeroes off — a ceiling of 25 should read "25", not "25.000000". */
function formatPct(pct: number): string {
  return String(Number(pct.toFixed(2)));
}

/**
 * Setting the ceiling.
 *
 * A modal rather than a field on the rename drawer, because it is the one thing on this screen
 * that stays editable when a role's name and code do not — putting it on a form that refuses to
 * open for system roles would have hidden it from exactly the two roles that ship with a cap.
 */
function DiscountCeilingModal({ role, onClose }: { role: Role; onClose: () => void }) {
  const [pct, setPct] = useState<number | null>(role.maxDiscountPct);

  const save = useApiMutation<Role, number>({
    mutationFn: (value) =>
      apiFetch<Role>(`/api/role/${role.id}/discount-ceiling`, {
        method: 'PUT',
        body: { maxDiscountPct: value },
      }),
    success: `${role.name} updated.`,
    invalidates: [rolesKey],
    onSuccess: onClose,
  });

  return (
    <Modal
      open
      title={`Discount cap — ${role.name}`}
      okText="Save"
      okButtonProps={{ loading: save.isPending, disabled: pct === null }}
      onOk={() => void save.mutateAsync(pct ?? 0).catch(() => undefined)}
      onCancel={onClose}
      maskClosable={false}
    >
      <div className="space-y-3.5 py-2">
        <p className="text-[13px] text-default">
          The largest discount someone holding this role may give. Whether they may discount at all
          is a separate grant, on the Permissions drawer.
        </p>

        <InputNumber
          value={pct}
          onChange={setPct}
          min={0}
          max={100}
          precision={2}
          addonAfter="%"
          size="large"
          className="font-mono-nums w-44"
          autoFocus
        />

        {!canDiscount(role) && (
          <Notice title="This role cannot discount">
            It holds no Discount grant, so this cap will not apply to anything until one is added.
          </Notice>
        )}
      </div>
    </Modal>
  );
}

interface RoleFormValues {
  code: string;
  name: string;
  description?: string | null;
  maxDiscountPct?: number;
}

/**
 * Create and rename.
 *
 * `code` is create-only, and the server does not even accept it on update: it is what a role
 * assignment, a support script and a seeded default all refer to. Renaming the display name is the
 * supported way to change what people call it.
 */
function RoleDrawer({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const [form] = Form.useForm<RoleFormValues>();
  const isEdit = editing.mode === 'edit';
  const existing = editing.mode === 'edit' ? editing.role : undefined;

  const save = useApiMutation<Role, RoleFormValues>({
    mutationFn: (raw) => {
      const values = blankToNull(raw);

      return isEdit
        ? apiFetch<Role>(`/api/role/${existing?.id}`, {
            method: 'PUT',
            body: { name: values.name, description: values.description },
          })
        : apiFetch<Role>('/api/role', { method: 'POST', body: values });
    },
    success: isEdit ? 'Role updated.' : 'Role created.',
    invalidates: [rolesKey],
    onSuccess: onClose,
  });

  return (
    <FormDrawer<RoleFormValues>
      title={isEdit ? `Rename ${existing?.name ?? 'role'}` : 'New role'}
      subtitle={
        isEdit ? undefined : 'A new role starts with no permissions — grant them once it exists.'
      }
      open
      onClose={onClose}
      form={form}
      initialValues={existing ?? { code: '', name: '', maxDiscountPct: 0 }}
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save changes' : 'Create role'}
      submitting={save.isPending}
      width={460}
    >
      <Form.Item
        name="code"
        label="Code"
        rules={[{ required: true, message: 'A code is required' }]}
        extra={
          isEdit
            ? 'Fixed at creation — assignments refer to it'
            : 'Upper case, e.g. COUNTER_STAFF. Fixed once created.'
        }
      >
        <Input disabled={isEdit} className="font-mono-nums" />
      </Form.Item>

      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: 'A name is required' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <Input.TextArea rows={3} placeholder="What this job is responsible for." />
      </Form.Item>

      {!isEdit && (
        <Form.Item
          name="maxDiscountPct"
          label="Maximum discount"
          extra="Zero means no discount. Whether the role may discount at all is a separate grant."
        >
          <InputNumber min={0} max={100} precision={2} addonAfter="%" className="w-40" />
        </Form.Item>
      )}
    </FormDrawer>
  );
}

/**
 * The Module x Action grid.
 *
 * Saved wholesale — the endpoint replaces a role's grants with exactly what it is sent — so the
 * grid holds a working set and only writes on Save. That is also why the drawer refuses to save
 * while the catalogue has not loaded: an empty grid submitted as-is would revoke everything.
 *
 * An administrator role is shown but not editable. It holds every permission implicitly,
 * *including modules that do not exist yet*, which is a stronger statement than any set of ticks
 * could make — rendering it as 324 ticked boxes would be a lie the next release breaks.
 */
function PermissionsDrawer({
  role,
  readOnly,
  onClose,
}: {
  role: Role;
  readOnly: boolean;
  onClose: () => void;
}) {
  const catalogue = useQuery({
    queryKey: ['role', 'catalogue'],
    queryFn: () => apiFetch<PermissionCatalogue>('/api/role/catalogue'),
    // The catalogue is compiled into the server; it cannot change while the app is open.
    staleTime: Infinity,
  });

  const [grants, setGrants] = useState<ReadonlySet<GrantKey>>(() => toGrantSet(role.permissions));
  const [filter, setFilter] = useState('');

  // Memoised because `?? []` is a fresh array on every render, which would make every dependent
  // memo below recompute — including one that runs over the whole grid.
  const empty = useMemo<string[]>(() => [], []);
  const modules = catalogue.data?.modules ?? empty;
  const actions = catalogue.data?.actions ?? empty;

  const visible = useMemo(
    () => modules.filter((module) => module.toLowerCase().includes(filter.trim().toLowerCase())),
    [modules, filter],
  );

  const orphans = useMemo(
    () => unlistedGrants(grants, modules, actions),
    [grants, modules, actions],
  );

  const save = useApiMutation<Role, void>({
    mutationFn: () =>
      apiFetch<Role>(`/api/role/${role.id}/permissions`, {
        method: 'PUT',
        body: { permissions: toPermissionList(grants) },
      }),
    success: `Permissions updated for ${role.name}.`,
    invalidates: [rolesKey],
    onSuccess: onClose,
  });

  const editable = !readOnly && !role.isAdministrator && catalogue.isSuccess;

  return (
    <div className="fixed inset-0 z-9999 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[var(--overlay-scrim)]" aria-hidden="true" />

      <div className="relative flex h-full w-full max-w-4xl flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg">
        <div className="flex items-center justify-between gap-4 border-b border-border-color p-4">
          <h2 className="flex items-center gap-2 text-base font-bold text-gray-900">
            <i className="icon icon-shield-check" />
            Permissions — {role.name}
          </h2>

          <div className="flex items-center gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!editable || save.isPending}
              onClick={() => void save.mutateAsync().catch(() => undefined)}
            >
              {save.isPending ? 'Saving…' : 'Save permissions'}
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {role.isAdministrator && (
            <Notice title="This role holds everything">
              An administrator role covers every module, including ones added in a later release.
              That is what stops a new module being invisible to the people who administer the
              system, and it is why there is nothing to tick here.
            </Notice>
          )}

          {readOnly && !role.isAdministrator && (
            <Notice tone="warning" title="Your subscription is read-only">
              Permissions can be reviewed but not changed until it is renewed.
            </Notice>
          )}

          {catalogue.isError && <ApiErrorAlert error={catalogue.error} />}

          {orphans.length > 0 && (
            <Notice
              tone="warning"
              title={`${orphans.length} grant${orphans.length === 1 ? '' : 's'} not shown below`}
            >
              This role holds {orphans.join(', ')}, which the current permission catalogue does not
              list. Saving from this screen will remove them.
            </Notice>
          )}

          {!role.isAdministrator && (
            <>
              <Input
                allowClear
                placeholder="Filter modules"
                prefix={<i className="icon icon-search text-default" />}
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                className="max-w-72"
              />

              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead>
                    <tr>
                      <th className="sticky top-0 min-w-48 border-b border-border-color bg-white px-2.5 py-2 text-left text-[11.5px] font-semibold whitespace-nowrap text-default">
                        Module
                      </th>
                      {actions.map((action) => (
                        <th
                          key={action}
                          className="sticky top-0 border-b border-border-color bg-white px-2.5 py-2 text-center text-[11.5px] font-semibold whitespace-nowrap text-default"
                        >
                          {action}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((module) => {
                      const state = moduleState(grants, module, actions);

                      return (
                        <tr key={module} className="hover:bg-light">
                          <td className="border-b border-border-color px-2.5 py-1.5 text-left">
                            <Checkbox
                              checked={state === 'all'}
                              indeterminate={state === 'some'}
                              disabled={!editable}
                              onChange={(event) =>
                                setGrants((current) =>
                                  setModule(current, module, actions, event.target.checked),
                                )
                              }
                            >
                              <span className="font-medium">{module}</span>
                            </Checkbox>
                          </td>
                          {actions.map((action) => (
                            <td
                              key={action}
                              className="border-b border-border-color px-2.5 py-1.5 text-center"
                            >
                              <Checkbox
                                checked={has(grants, module, action)}
                                disabled={!editable}
                                aria-label={`${module} ${action}`}
                                onChange={() =>
                                  setGrants((current) => toggle(current, module, action))
                                }
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {catalogue.isSuccess && visible.length === 0 && (
                  <EmptyState
                    icon="icon icon-search"
                    title={`No module matches "${filter}"`}
                    hint="The catalogue is compiled into the server — if a module is missing here, it does not exist yet."
                  />
                )}
              </div>

              <p className="text-[12px] text-default">
                View, Export and Print keep working while a subscription is read-only; everything
                else, including Discount, is treated as a write. Whether a module appears at all
                also depends on the plan — permission and plan are two separate questions.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
